import type { EventWebsiteData, ProductKey } from "@/models"
import type { EventVaultPageMode, IEventVaultRequest, IEventVaultResponse, IEventVaultResponseV1, ITemplateEventVaultResponse, WorkaroundType } from "@/types"
import { i18n } from "@/i18n"
import { EventTemplate, EventVaultSettings, Location, ZipSummary } from "@/models"

export enum EventVaultStatus {
  Draft = "draft", // Disabled uploads
  Ready = "ready", // Disabled uploads
  Live = "live", // Enabled uploads
  Closed = "closed", // Disabled uploads
  Expired = "expired", // Disabled uploads
  Archived = "archived", // Disabled uploads
  All = "all", // I don't even know what this is??
  Expiring = "expiring", // Expiring soon
  DeletingInProgress = "deleting", // Deletion is progress
  ExtendInProgress = "extending", // Extend in progress
}

export enum EventVaultType {
  Wedding = 1,
  EngagementParty = 2,
  Party = 3,
  Corporate = 4,
  Holiday = 5,
  Festival = 6,
  Other = 7,
  Memorial = 8,
}

export class EventVault {
  public id?: number
  public packageSubscriptionId?: number
  public pageMode?: EventVaultPageMode
  public isHost?: boolean // True if the user is the host of the event
  public isGuest?: boolean // True if the user is a guest with a valid cookie for the event
  public title: string
  public eventType?: EventVaultType
  public eventHeader?: string
  public coverImage?: File | null
  public coverImageUrl?: string
  public welcomeVideo?: File | null
  public welcomeVideoUrl?: string
  public description?: string
  public eventVaultSettings?: EventVaultSettings
  public eventTemplate?: EventTemplate
  public status: EventVaultStatus = EventVaultStatus.Draft
  public eventDate?: Date
  public eventEndDate?: Date
  public closeDate?: Date
  public accessedDate?: Date
  public timezone?: string
  public expiryDate?: Date
  public zipSummary?: ZipSummary
  public totalGuests?: number
  public totalPics?: number
  public totalVids?: number
  public totalMessages?: number
  public totalAlbums?: number
  public qrCodeUrl?: string
  public shareLink?: string
  public invitationLink?: string
  public protectedLink?: string
  public passcode?: string
  public productKey?: ProductKey
  public productName?: string
  public location?: Location
  public directUpload?: boolean = false
  public upgradeUrl?: string
  public freemiumMode?: boolean = false
  public maxPhotoLimit?: number
  public photoUploadCount?: number
  public communicationLocale?: string
  public website?: EventWebsiteData

  public workaround?: {
    redirectUrl?: string
    type?: WorkaroundType
  }

  constructor(eventVault: Partial<EventVault>) {
    this.id = eventVault.id
    this.packageSubscriptionId = eventVault.packageSubscriptionId
    this.pageMode = eventVault.pageMode
    this.isHost = eventVault.isHost
    this.isGuest = eventVault.isGuest
    this.workaround = eventVault.workaround
    this.title = eventVault.title ?? "Unknown Event"
    this.eventType = eventVault.eventType
    this.eventHeader = eventVault.eventHeader
    this.description = eventVault.description
    this.coverImage = eventVault.coverImage
    this.coverImageUrl = eventVault.coverImageUrl
    this.welcomeVideo = eventVault.welcomeVideo
    this.welcomeVideoUrl = eventVault.welcomeVideoUrl
    this.eventVaultSettings = eventVault.eventVaultSettings
    this.eventTemplate = eventVault.eventTemplate
    this.status = eventVault.status ?? EventVaultStatus.Draft
    this.eventDate = eventVault.eventDate
    this.eventEndDate = eventVault.eventEndDate
    this.accessedDate = eventVault.accessedDate
    this.closeDate = eventVault.closeDate
    this.timezone = eventVault.timezone
    this.expiryDate = eventVault.expiryDate
    this.zipSummary = eventVault.zipSummary
    this.totalGuests = eventVault.totalGuests
    this.totalPics = eventVault.totalPics
    this.totalVids = eventVault.totalVids
    this.totalMessages = eventVault.totalMessages
    this.totalAlbums = eventVault.totalAlbums
    this.qrCodeUrl = eventVault.qrCodeUrl
    this.shareLink = eventVault.shareLink
    this.invitationLink = eventVault.invitationLink
    this.protectedLink = eventVault.protectedLink
    this.passcode = eventVault.passcode
    this.productKey = eventVault.productKey
    this.productName = eventVault.productName
    this.location = eventVault.location
    this.directUpload = eventVault.directUpload ?? false
    this.upgradeUrl = eventVault.upgradeUrl
    this.freemiumMode = eventVault.freemiumMode ?? false
    this.maxPhotoLimit = eventVault.maxPhotoLimit
    this.photoUploadCount = eventVault.photoUploadCount
    this.communicationLocale = eventVault.communicationLocale
    this.website = eventVault.website
  }

  public static fromResponseV1(response?: IEventVaultResponseV1) {
    let status = EventVaultStatus.Draft

    switch (response?.eventVaultStatus) {
      case 1:
        status = EventVaultStatus.Ready
        break
      case 2:
        status = EventVaultStatus.Live
        break
      case 3:
        status = EventVaultStatus.Closed
        break
      case 4:
        status = EventVaultStatus.Expired
        break
      case 5:
        status = EventVaultStatus.Archived
        break
      case 6:
        status = EventVaultStatus.All
        break
      case 7:
        status = EventVaultStatus.Expiring
        break
      case 8:
        status = EventVaultStatus.DeletingInProgress
        break
      case 9:
        status = EventVaultStatus.ExtendInProgress
        break
      default:
        status = EventVaultStatus.Draft
        break
    }

    const id = response?.eventVaultId
    const eventDate = response?.eventDate ? new Date(response.eventDate) : undefined
    const eventEndDate = response?.eventEndDate ? new Date(response.eventEndDate) : undefined
    const accessedDate = response?.accessedDate ? new Date(response.accessedDate) : undefined
    const closeDate = response?.eventCloseDate ? new Date(response.eventCloseDate) : undefined
    const expiryDate = response?.eventExpiryDate ? new Date(response.eventExpiryDate) : undefined
    const shareLink = (id && response?.accessToken) ? `/guest/${id}/access/${response?.accessToken}` : undefined
    const protectedLink = `/guest/${id}/login`

    return new EventVault({
      id,
      pageMode: response?.pageMode,
      isHost: response?.isHost ?? false,
      isGuest: response?.isGuest ?? false,
      title: response?.title ?? "Unknown Event",
      eventType: response?.eventVaultTypeId,
      productKey: response?.gpxProductKey,
      productName: response?.gpxProductName,
      eventHeader: response?.eventHeader,
      coverImageUrl: response?.featureImageUrl,
      welcomeVideoUrl: response?.pathToWelcomeVideo,
      description: response?.description,
      totalGuests: response?.eventGuestCount,
      totalPics: response?.totalPics,
      totalVids: response?.totalVids,
      totalMessages: response?.totalMessages,
      totalAlbums: response?.totalAlbums,
      qrCodeUrl: response?.qrCodeUrl,
      shareLink,
      protectedLink,
      passcode: response?.passcode,
      timezone: response?.timezone,
      status,
      eventDate,
      eventEndDate,
      closeDate,
      accessedDate,
      expiryDate,
      upgradeUrl: response?.upgradeUrl,
      freemiumMode: response?.freemiumMode ?? false,
      maxPhotoLimit: response?.MaxPhotos,
      photoUploadCount: response?.PhotoCount,
      communicationLocale: response?.communicationLocale,
    })
  }

  public static fromResponse(response?: IEventVaultResponse) {
    let status = EventVaultStatus.Draft

    switch (response?.EventVaultStatus) {
      case 1:
        status = EventVaultStatus.Ready
        break
      case 2:
        status = EventVaultStatus.Live
        break
      case 3:
        status = EventVaultStatus.Closed
        break
      case 4:
        status = EventVaultStatus.Expired
        break
      case 5:
        status = EventVaultStatus.Archived
        break
      case 6:
        status = EventVaultStatus.All
        break
      case 7:
        status = EventVaultStatus.Expiring
        break
      case 8:
        status = EventVaultStatus.DeletingInProgress
        break
      case 9:
        status = EventVaultStatus.ExtendInProgress
        break
      default:
        status = EventVaultStatus.Draft
        break
    }

    const id = response?.EventVaultID
    const packageSubscriptionId = response?.PackageSubscriptionID
    const eventDate = response?.EventDate ? new Date(response.EventDate) : undefined
    const eventEndDate = response?.EventEndDate ? new Date(response.EventEndDate) : undefined
    const accessedDate = response?.AccessedDate ? new Date(response.AccessedDate) : undefined
    const closeDate = response?.EventCloseDate ? new Date(response.EventCloseDate) : undefined
    const expiryDate = response?.EventExpiryDate ? new Date(response.EventExpiryDate) : undefined
    const zipSummary = response?.ZipJobSummary ? ZipSummary.fromResponse(response.ZipJobSummary) : undefined
    const eventTemplate = response?.EventTemplate ? EventTemplate.fromResponse(response?.EventTemplate) : undefined
    const location = response?.Location ? Location.fromResponse(response.Location) : undefined
    const eventVaultSettings = response?.EventVaultSettings
      ? EventVaultSettings.fromResponse(response?.EventVaultSettings)
      : undefined
    const shareLink = (id && response?.AccessToken) ? `/guest/${id}/access/${response?.AccessToken}` : undefined
    const invitationLink = (id && response?.AccessToken) ? `/guest/${id}/invitation?accessToken=${response?.AccessToken}` : undefined
    const protectedLink = `/guest/${id}/login`

    const workaround = response?.Workaround
      ? { redirectUrl: response?.Workaround.RedirectUrl, type: response?.Workaround.Type }
      : undefined

    const website: EventWebsiteData | undefined = response?.EventWebsite
      ? {
          domainKey: response.EventWebsite.DomainKey ?? "",
          slug: response.EventWebsite.Slug ?? "",
          isPublic: Boolean(response.EventWebsite.IsPublic),
          dateCreated: response.EventWebsite.DateCreated ? new Date(response.EventWebsite.DateCreated) : undefined,
          lastUpdated: response.EventWebsite.LastUpdated ? new Date(response.EventWebsite.LastUpdated) : undefined,
        }
      : undefined

    return new EventVault({
      id,
      packageSubscriptionId,
      pageMode: response?.PageMode,
      isHost: response?.IsHost ?? false,
      isGuest: response?.IsGuest ?? false,
      workaround,
      title: response?.Title ?? "Unknown Event",
      eventType: response?.EventVaultTypeID,
      productKey: response?.GPXProductKey,
      productName: response?.GPXProductName,
      eventHeader: response?.EventHeader,
      coverImageUrl: response?.PathToFeatureImage,
      welcomeVideoUrl: response?.PathToWelcomeVideo,
      description: response?.Description,
      totalGuests: response?.EventGuestCount,
      totalPics: response?.TotalPics,
      totalVids: response?.TotalVids,
      totalMessages: response?.TotalMessages,
      totalAlbums: response?.TotalAlbums,
      qrCodeUrl: response?.FullPathToQRCode,
      shareLink,
      invitationLink,
      protectedLink,
      passcode: response?.Passcode,
      timezone: response?.Timezone,
      eventVaultSettings,
      eventTemplate,
      status,
      eventDate,
      eventEndDate,
      closeDate,
      accessedDate,
      expiryDate,
      zipSummary,
      location,
      directUpload: response?.UseDirectUpload ?? false,
      upgradeUrl: response?.UpgradeUrl,
      freemiumMode: response?.FreemiumMode ?? false,
      maxPhotoLimit: response?.MaxPhotos,
      photoUploadCount: response?.PhotoCount,
      communicationLocale: response?.CommunicationLocale,
      website,
    })
  }

  public static toRequest(eventVault: Partial<EventVault>): IEventVaultRequest {
    return {
      Title: eventVault.title?.trim() ?? "",
      EventHeader: eventVault.eventHeader?.trim() ?? "",
      Description: eventVault.description?.trim(),
      EventDate: eventVault.timezone ? eventVault.eventDate?.toISOString() : eventVault.eventDate?.toDateString(),
      EventEndDate: eventVault.timezone ? eventVault.eventEndDate?.toISOString() : eventVault.eventEndDate?.toDateString(),
      Timezone: eventVault.timezone,
      CoverImage: eventVault.coverImage,
      CoverImageUrl: eventVault.coverImageUrl,
      WelcomeVideo: eventVault.welcomeVideo,
      WelcomeVideoUrl: eventVault.welcomeVideoUrl,
      EventVaultTypeId: eventVault.eventType,
      Location: eventVault.location ? Location.toRequest(eventVault.location) : undefined,
      CommunicationLocale: eventVault.communicationLocale,
    }
  }
}

export class TemplateEventVault extends EventVault {
  public isApplied: boolean = false

  constructor(eventVault: Partial<TemplateEventVault>) {
    super(eventVault)
    this.isApplied = eventVault.isApplied ?? false
  }

  public static fromResponse(response?: ITemplateEventVaultResponse) {
    return new TemplateEventVault({
      ...EventVault.fromResponse(response),
      isApplied: response?.IsApplied ?? false,
    })
  }
}

// eslint-disable-next-line ts/no-namespace
export namespace EventVaultStatus {
  export function getLabel(status: EventVaultStatus) {
    const { t } = i18n.global

    switch (status) {
      case EventVaultStatus.Draft:
        return t("event_vault_status.draft")
      case EventVaultStatus.Ready:
        return t("event_vault_status.ready")
      case EventVaultStatus.Live:
        return t("event_vault_status.live")
      case EventVaultStatus.Closed:
        return t("event_vault_status.closed")
      case EventVaultStatus.Expired:
        return t("event_vault_status.expired")
      case EventVaultStatus.Archived:
        return t("event_vault_status.archived")
      case EventVaultStatus.Expiring:
        return t("event_vault_status.expiring")
      case EventVaultStatus.ExtendInProgress:
        return t("event_vault_status.extending")
      case EventVaultStatus.DeletingInProgress:
        return t("event_vault_status.deleting")
      case EventVaultStatus.All:
        return t("event_vault_status.all")
      default:
        return ""
    }
  }
}
